import {Enums} from "../../../thor.enums";
import {find} from "lodash";
import M from "materialize-css";

export class ImportDialogController {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$mdMedia",
        "$scope",
        "$translate",
        "importMedia",
        "importPasserelle",
        "importSaleItems",
        "importSaleResult"
    ];

    public title: string;
    public gauges;
    public importStep: Enums.ImportItemStep = Enums.ImportItemStep.NotStarted;
    public progress;
    public summary;
    public summaryTemplate;
    public keepItemsAfterImport: boolean = false;
    public ImportItemStep = Enums.ImportItemStep;
    public hasSecretOrders: boolean;
    public countPremiumError: number;

    // bind from parent (AddItemController::_createUploadDialog)
    private sale: any;
    private engineData: any;
    private engineType: string;

    private action;
    private $t;

    constructor(
        private $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        public $mdMedia: ng.material.IMedia,
        $scope,
        $translate: ng.translate.ITranslateService,
        private importMedia,
        private importPasserelle,
        private importSaleItems,
        private importSaleResult
    ) {
        this.$t = $translate.instant;
        this.progress = 0;

        if (this.sale && this.engineData && this.engineType) {
            this.prepareEngine();
            if (this.action && this.importStep !== Enums.ImportItemStep.WaitForUserAction) {
                this.engine();
            }
        }

        $scope.$watch(
            () => this.summary?.errors,
            current => (this.countPremiumError = current?.filter(row => row.premium_error).length || 0),
            true
        );
    }

    public finish() {
        if (this.importStep === Enums.ImportItemStep.Importing) {
            return;
        }
        this.$mdDialog.hide();
    }

    public engine() {
        if (this.importStep === Enums.ImportItemStep.Importing) {
            this.$log.warn("Import already in progress, ignoring new start request.");
            return;
        }
        this.$log.info(`Import start to SaleId : ${this.sale.id}`);
        this.importStep = Enums.ImportItemStep.Importing;

        this.importPasserelle.keepItemsAfterImport = this.keepItemsAfterImport;

        this.action()
            .catch(error => {
                this.$log.error(`Error while importing ${this.engineType}`, error);
            })
            .finally(
                () => {
                    angular.element(document).ready(() => {
                        const elems = document.querySelectorAll(".collapsible");
                        M.Collapsible.init(elems, {});
                    });

                    this.importStep = Enums.ImportItemStep.Finished;
                    return this.sale.fetchAll();
                },
                notification => {
                    this.onNotify(notification);
                }
            );
    }

    private prepareEngine() {
        this.title = "";
        this.action = null;
        this.summaryTemplate = null;
        this.gauges = {};

        if (this.engineType === "csvSaleResult") {
            this.title = this.$t("import_des_resultats_de_vente");
            this.action =
                this.importSaleResult.engine.bind(this.importSaleResult, this.sale, this.engineData);
            this.summaryTemplate = "import-sale-result-summary";
            this.gauges = {
                rows: {
                    icon: "insert_drive_file",
                    counter: {
                        total: 0,
                        valid: 0,
                        error: 0
                    }
                }
            };
        }

        if (this.engineType === "csvSaleItems") {
            this.title = this.$t("import_a_partir_d_un_csv");
            this.action =
                this.importSaleItems.engine.bind(this.importSaleItems, this.sale, this.engineData);
            this.summaryTemplate = "import-sale-items-summary";
            this.gauges = {
                rows: {
                    icon: "insert_drive_file",
                    counter: {
                        total: 0,
                        valid: 0,
                        error: 0
                    }
                }
            };
        }

        if (this.engineType === "medias") {
            this.title = this.$t("import_a_partir_de_photos");
            this.action =
                this.importMedia.engine.bind(this.importMedia, this.sale, this.engineData);
            this.summaryTemplate = "import-media-summary";
            this.gauges = {
                medias: {
                    icon: "insert_photo",
                    counter: {
                        total: this.engineData.length,
                        valid: 0,
                        error: 0
                    }
                }
            };
        }

        if (this.engineType === "zipPasserelle" || this.engineType === "xmlPasserelle") {
            this.title = this.$t("import_passerelle");
            this.summaryTemplate = "import-passerelle-summary";
            this.gauges = {
                medias: {
                    icon: "insert_photo",
                    counter: {
                        total: 0,
                        valid: 0,
                        error: 0
                    }
                },
                items: {
                    icon: "insert_drive_file",
                    counter: {
                        total: 0,
                        valid: 0,
                        error: 0
                    }
                },
                extraItems: {
                    icon: "delete",
                    counter: {
                        total: 0,
                        valid: 0,
                        error: 0
                    }
                }
            };

            if (this.sale.items.length > 0) {
                this.importStep = Enums.ImportItemStep.WaitForUserAction;

                this.hasSecretOrders = !!find(this.sale.items, item => item.secretOrderCount > 0);
            }
        }

        if (this.engineType === "zipPasserelle") {
            this.action =
                this.importPasserelle.engineZipFile.bind(this.importPasserelle, this.sale, this.engineData);
        }

        if (this.engineType === "xmlPasserelle") {
            this.action =
                this.importPasserelle.engineXmlFile.bind(this.importPasserelle, this.sale, this.engineData);
        }
    }

    private onNotify(notification) {
        switch (notification.action) {
            case "incrementGauge":
                this.incrementGauge(notification.data);
                break;
            case "setGauge":
                this.setGauge(notification.data);
                break;
            case "setSummary":
                this.summary = notification.data;
                break;
            default:
                this.$log.error(`onNotify:: action: ${notification.action} is not implemented`);
                break;
        }
    }

    private incrementGauge(data) {
        const increment = data.increment || 1;
        this.gauges[data.gauge].counter[data.counter] += increment;
        this.updateProgress();
    }

    private setGauge(data) {
        this.gauges[data.gauge].counter[data.counter] = data.value;
        this.updateProgress();
    }

    private updateProgress() {
        let denominator = 0;
        let numerator = 0;
        for (const gaugeName in this.gauges) {
            if (this.gauges.hasOwnProperty(gaugeName) && this.gauges[gaugeName]) {
                const counter = this.gauges[gaugeName].counter;
                denominator += counter.total;
                numerator += counter.valid + counter.error;
            }
        }

        let progress = Math.ceil(100 * numerator / denominator);
        if (progress > 100) { progress = 100; }
        this.progress = progress;
    }
}

